import React, { useCallback, useEffect, useState } from "react";
import { Pencil, Plus, Trash2, UserPlus } from "lucide-react";
import api from "../../../services/apiService";
import DataTable, {
  createActionsColumn,
  DataTableButton,
  DataTableFilters,
  DataTableSelect,
} from "../../../components/data-table";
import AlertNotification from "../../../components/notification";
import DeleteModal from "../../../components/models/deleteModal.jsx";
import HrAssetModal from "./HrAssetModal";
import HrAssetAssignModal from "./HrAssetAssignModal";

const authHeaders = () => {
  const token = localStorage.getItem("token");
  return { Authorization: token ? `Bearer ${token}` : undefined };
};

const STATUS_BADGE = {
  available: "bg-green-50 border-green-200 text-green-700",
  assigned:  "bg-blue-50 border-blue-200 text-blue-700",
  repair:    "bg-yellow-50 border-yellow-200 text-yellow-700",
  lost:      "bg-red-50 border-red-200 text-red-700",
  retired:   "bg-gray-100 border-gray-200 text-gray-500",
};

const ASSET_TYPES = ["laptop","monitor","mobile","chair","table","accessory","other"];

function HrAssets() {
  const [assets, setAssets]       = useState([]);
  const [locations, setLocations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState(null);

  const [filterType, setFilterType]     = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterLoc, setFilterLoc]       = useState("");

  const [showAssetModal, setShowAssetModal]   = useState(false);
  const [editingAsset, setEditingAsset]       = useState(null);
  const [isSubmitting, setIsSubmitting]       = useState(false);
  const [submitError, setSubmitError]         = useState("");

  const [assignTarget, setAssignTarget]       = useState(null);
  const [isAssigning, setIsAssigning]         = useState(false);
  const [assignError, setAssignError]         = useState("");

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting]     = useState(false);

  const fetchAssets = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = {};
      if (filterType)   params.type       = filterType;
      if (filterStatus) params.status     = filterStatus;
      if (filterLoc)    params.locationId = filterLoc;
      const res = await api.get("/hr/assets", { params, headers: authHeaders() });
      setAssets(Array.isArray(res.data) ? res.data : []);
    } catch { setNotification({ text: "Failed to load assets.", status: false }); }
    finally { setIsLoading(false); }
  }, [filterType, filterStatus, filterLoc]);

  useEffect(() => { fetchAssets(); }, [fetchAssets]);

  useEffect(() => {
    api.get("/hr/locations", { params: { activeOnly: true }, headers: authHeaders() })
       .then((r) => setLocations(Array.isArray(r.data) ? r.data : []));
    api.get("/hr/employees", { headers: authHeaders() })
       .then((r) => setEmployees(Array.isArray(r.data) ? r.data : []));
  }, []);

  const handleAssetSubmit = async (form) => {
    setIsSubmitting(true); setSubmitError("");
    try {
      if (editingAsset) {
        await api.put(`/hr/assets/${editingAsset.id}`, form, { headers: authHeaders() });
        setNotification({ text: "Asset updated.", status: true });
      } else {
        await api.post("/hr/assets", form, { headers: authHeaders() });
        setNotification({ text: "Asset added.", status: true });
      }
      setShowAssetModal(false); setEditingAsset(null);
      fetchAssets();
    } catch (err) { setSubmitError(err?.response?.data?.message || "Failed to save asset."); }
    finally { setIsSubmitting(false); }
  };

  const handleAssign = async (form) => {
    if (!assignTarget) return;
    setIsAssigning(true); setAssignError("");
    try {
      await api.post("/hr/asset-assignments", { ...form, assetId: assignTarget.id }, { headers: authHeaders() });
      setNotification({ text: "Asset assigned successfully.", status: true });
      setAssignTarget(null);
      fetchAssets();
    } catch (err) { setAssignError(err?.response?.data?.message || "Failed to assign asset."); }
    finally { setIsAssigning(false); }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await api.delete(`/hr/assets/${deleteTarget.id}`, { headers: authHeaders() });
      setNotification({ text: "Asset deleted.", status: true });
      setDeleteTarget(null); fetchAssets();
    } catch { setNotification({ text: "Failed to delete asset.", status: false }); }
    finally { setIsDeleting(false); }
  };

  const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);

  const columns = [
    { key: "assetCode", label: "Code", sortable: true, searchable: true,
      render: (v) => <span className="font-mono text-xs text-app-secondary">{v || "—"}</span> },
    { key: "name", label: "Name", sortable: true, searchable: true,
      render: (v) => <span className="font-medium text-app-secondary">{v}</span> },
    { key: "type", label: "Type", sortable: true, render: (v) => <span className="capitalize">{v}</span> },
    { key: "brand", label: "Brand", sortable: true, searchable: true, render: (v) => v || "—" },
    { key: "location.name", label: "Location", sortable: true, render: (v) => v || "—" },
    { key: "condition", label: "Condition", sortable: true, render: (v) => <span className="capitalize">{v}</span> },
    { key: "status", label: "Status", sortable: true,
      render: (v) => (
        <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold capitalize ${STATUS_BADGE[v] || ""}`}>
          {v}
        </span>
      ) },
    { key: "assignedTo", label: "Assigned To", sortable: true, searchable: true,
      accessor: (asset) => asset.assignments?.[0]?.employee?.fullName,
      render: (v) => <span className="text-xs">{v || "—"}</span> },
    createActionsColumn((asset) => [
      { label: "Assign", icon: UserPlus, hidden: asset.status !== "available",
        onSelect: () => { setAssignTarget(asset); setAssignError(""); } },
      { label: "Edit", icon: Pencil,
        onSelect: () => { setEditingAsset(asset); setSubmitError(""); setShowAssetModal(true); } },
      { label: "Delete", icon: Trash2, tone: "danger", onSelect: () => setDeleteTarget(asset) },
    ], { getRowLabel: (asset) => asset.name }),
  ];

  return (
    <div className="space-y-5">
      <p className="text-sm text-app-muted">Manage company assets and assignments.</p>

      <DataTable
        ariaLabel="Assets"
        columns={columns}
        data={assets}
        loading={isLoading}
        loadingRowCount={8}
        searchable
        searchPlaceholder="Search code, name, brand or assignee"
        filterable
        activeFilterCount={[filterType, filterStatus, filterLoc].filter(Boolean).length}
        filterContent={
          <DataTableFilters
            canClear={Boolean(filterType || filterStatus || filterLoc)}
            onClear={() => { setFilterType(""); setFilterStatus(""); setFilterLoc(""); }}
          >
            <DataTableSelect
              id="hr-assets-type"
              label="Type"
              value={filterType}
              onChange={setFilterType}
              options={[{ value: "", label: "All Types" }, ...ASSET_TYPES.map((t) => ({ value: t, label: capitalize(t) }))]}
            />
            <DataTableSelect
              id="hr-assets-status"
              label="Status"
              value={filterStatus}
              onChange={setFilterStatus}
              options={[
                { value: "", label: "All Status" },
                ...["available", "assigned", "repair", "lost", "retired"].map((s) => ({ value: s, label: capitalize(s) })),
              ]}
            />
            <DataTableSelect
              id="hr-assets-location"
              label="Location"
              value={filterLoc}
              onChange={setFilterLoc}
              options={[{ value: "", label: "All Locations" }, ...locations.map((l) => ({ value: l.id, label: l.name }))]}
            />
          </DataTableFilters>
        }
        toolbarRight={
          <DataTableButton
            variant="primary"
            icon={Plus}
            onClick={() => { setEditingAsset(null); setSubmitError(""); setShowAssetModal(true); }}
          >
            Add Asset
          </DataTableButton>
        }
        pagination
        defaultPageSize={10}
        labels={{ empty: "No assets found." }}
      />

      {notification && <AlertNotification text={notification.text} status={notification.status} onClose={() => setNotification(null)} />}

      <HrAssetModal
        key={editingAsset?.id ?? "new"}
        show={showAssetModal}
        onClose={() => { if (!isSubmitting) { setShowAssetModal(false); setEditingAsset(null); } }}
        onSubmit={handleAssetSubmit}
        initial={editingAsset}
        locations={locations}
        isSubmitting={isSubmitting}
        error={submitError}
      />

      <HrAssetAssignModal
        key={assignTarget?.id ?? "assign"}
        show={Boolean(assignTarget)}
        onClose={() => { if (!isAssigning) setAssignTarget(null); }}
        onSubmit={handleAssign}
        employees={employees}
        isSubmitting={isAssigning}
        error={assignError}
      />

      <DeleteModal
        show={Boolean(deleteTarget)}
        handleClose={() => { if (!isDeleting) setDeleteTarget(null); }}
        handleConfirm={handleDelete}
        message={`Delete asset "${deleteTarget?.name}"?`}
        loading={isDeleting}
      />
    </div>
  );
}

export default HrAssets;
